import { useMemo, useSyncExternalStore } from 'react';
import { extractPreviewDocs } from '../../../lib/preview-doc';
import { previewKindIcon, previewKindClass } from '../../../lib/file-icon';
import { useChatPreviewStore } from '../../../store/chatPreviewStore';
import type { ChatMessageEvent } from '../../../types/chat';
import { tChat, getChatLang, subscribeChatLang } from '../../../lib/chat-i18n';

/**
 * MarkdownPreviewTriggers (chat-doc-preview — 2026-06-30, formats 확장 2026-07-01) — 메시지에
 * 링크/첨부된 미리보기 가능 문서(md·pdf·docx·xlsx)마다 "미리보기" 버튼을 렌더. 클릭 시 우측
 * ChatDocPreviewPanel 을 연다(chatPreviewStore).
 *
 * additive — 미리보기 문서가 없는 메시지는 null 렌더(영향 0). MessageItem 의 첨부 영역 아래에 배치.
 */
export function MarkdownPreviewTriggers({ message }: { message: ChatMessageEvent }) {
  useSyncExternalStore(subscribeChatLang, getChatLang, getChatLang);
  const openPreview = useChatPreviewStore((s) => s.openPreview);
  const openKey = useChatPreviewStore((s) => s.open?.doc.key ?? null);
  // 본문/첨부가 바뀔 때만 재계산 (hover 리렌더에는 불변).
  // chat-file-preview (2026-07-02) — 첨부 미리보기는 파일 카드 클릭 → 미리보기 패널/경고 모달로 이동.
  // 여기서는 **본문 텍스트의 .md 링크**(클릭할 파일 카드가 없는 것)만 칩으로 남긴다.
  const docs = useMemo(
    () => extractPreviewDocs(message).filter((d) => d.source.kind === 'url'),
    [message.id, message.content, message.attachments],
  );
  if (docs.length === 0) return null;

  return (
    <div className="chat-md-preview-triggers">
      {docs.map((doc) => {
        const KindIcon = previewKindIcon(doc.kind);
        return (
          <button
            key={doc.key}
            type="button"
            className={`chat-md-preview-trigger${openKey === doc.key ? ' is-active' : ''}`}
            onClick={() => openPreview(message.roomId, doc)}
            aria-label={tChat('chat.preview.trigger_aria', { name: doc.title })}
            aria-pressed={openKey === doc.key}
          >
            <KindIcon size={13} className={previewKindClass(doc.kind)} aria-hidden />
            <span className="chat-md-preview-trigger-name">{doc.title}</span>
            <span className="chat-md-preview-trigger-label">{tChat('chat.preview.trigger')}</span>
          </button>
        );
      })}
    </div>
  );
}
